import React from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import {
    Checkbox,
    MessageBar,
    Pivot,
    PivotItem,
    Stack,
    TextField,
} from '@fluentui/react';
import { buildGlobalSettingsPatch, IGlobalStorefrontSettings, STOREFRONT_ASSET_GLOBAL } from '../../../../models/Storefront';
import {
    useGetGlobalStorefrontSettingsQuery,
    usePatchGlobalStorefrontSettingsMutation,
} from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import { useGetOrganizationEntitlementsQuery } from '../../../../store/endpoints/OrganizationsEndpoints';
import { RootState } from '../../../../store/rootReducer';
import { STOREFRONT_TIER_ENTERPRISE } from '../../../../models/Settings';
import ToggleField from '../../../../components/Common/ToggleField';
import NumberField from '../../../../components/Inputs/NumberField';
import ImageEditor from '../../../../components/Inputs/ImageEditor';
import FontPicker from '../../../../components/Inputs/FontPicker';
import CompactColorPicker from '../../../../components/Inputs/CompactColorPicker';
import RichTextField from '../../../../components/Common/RichTextField';
import StorefrontThemeSelection from '../../../../components/Storefront/StorefrontThemeSelection';
import StorefrontBannerEditor from '../../../../components/Storefront/StorefrontBannerEditor';
import StorefrontLinksEditor from '../../../../components/Storefront/StorefrontLinksEditor';
import StorefrontThemeManagement from '../../../../components/Storefront/Themes/StorefrontThemeManagement';
import { AssetSourceProvider } from '../../../../components/Storefront/Assets/AssetSourceContext';
import Setting from '../../../../components/Common/Setting';
import ConditionalSetting from '../../../../components/Common/ConditionalSetting';
import { useUrlTabState } from '../../../../hooks/useUrlState';

const DEBOUNCE_MS = 3000;

// The footer field stack, gated on Default Include Footer - kept as one list so the
// ConditionalSetting wrapper below and the registry's dependsOn entries stay obviously in step.
const GLOBAL_FOOTER_FIELD_IDS = [
    'storefront.appearance.footerLogo',
    'storefront.appearance.footerContactDetails',
    'storefront.appearance.footerText',
    'storefront.appearance.footerPhone',
    'storefront.appearance.footerEmail',
    'storefront.appearance.footerFacebook',
    'storefront.appearance.footerYouTube',
    'storefront.appearance.footerLinkedIn',
    'storefront.appearance.footerTwitter',
    'storefront.appearance.footerX',
    'storefront.appearance.footerBluesky',
] as const;

// The four colors that only apply when Theme is Custom.
const GLOBAL_THEME_CUSTOM_FIELD_IDS = [
    'storefront.appearance.menuBarColor',
    'storefront.appearance.menuBarTextColor',
    'storefront.appearance.backgroundColor',
    'storefront.appearance.textColor',
] as const;

const StorefrontGlobalAppearancePage: React.FC = () => {
    const globalSettingsQuery = useGetGlobalStorefrontSettingsQuery();
    const [tab, setTab] = useUrlTabState('headerFooter');
    const [patchGlobalSettings] = usePatchGlobalStorefrontSettingsMutation();

    const [localSettings, setLocalSettingsRaw] = React.useState<IGlobalStorefrontSettings>();
    const localSettingsRef = React.useRef<IGlobalStorefrontSettings>();
    const originalSettingsRef = React.useRef<IGlobalStorefrontSettings>();
    const debounceSaveRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const orgEntitlements = useGetOrganizationEntitlementsQuery(selectedOrganization ?? '', {
        skip: !selectedOrganization || selectedOrganization === '',
    });

    const setLocalSettings = React.useCallback((s: IGlobalStorefrontSettings) => {
        localSettingsRef.current = s;
        setLocalSettingsRaw(s);
    }, []);

    React.useEffect(() => {
        if (globalSettingsQuery.data && !originalSettingsRef.current) {
            setLocalSettings(globalSettingsQuery.data);
            originalSettingsRef.current = globalSettingsQuery.data;
        }
    }, [globalSettingsQuery.data, setLocalSettings]);

    React.useEffect(() => {
        return () => {
            if (debounceSaveRef.current) {
                clearTimeout(debounceSaveRef.current);
                if (localSettingsRef.current && originalSettingsRef.current) {
                    const patch = buildGlobalSettingsPatch(originalSettingsRef.current, localSettingsRef.current);
                    if (Object.keys(patch).length > 0) {
                        patchGlobalSettings(patch);
                    }
                }
            }
        };
    }, []);

    const doSave = React.useCallback(async (current: IGlobalStorefrontSettings) => {
        if (!originalSettingsRef.current) return;
        const patch = buildGlobalSettingsPatch(originalSettingsRef.current, current);
        if (Object.keys(patch).length === 0) return;
        try {
            await patchGlobalSettings(patch).unwrap();
            originalSettingsRef.current = { ...originalSettingsRef.current, ...patch };
            toast.success('Settings saved');
        } catch {
            toast.error('Failed to save settings');
        }
    }, [patchGlobalSettings]);

    const scheduleSave = React.useCallback(() => {
        if (debounceSaveRef.current) clearTimeout(debounceSaveRef.current);
        debounceSaveRef.current = setTimeout(() => {
            if (localSettingsRef.current) doSave(localSettingsRef.current);
        }, DEBOUNCE_MS);
    }, [doSave]);

    const saveNow = React.useCallback(() => {
        if (debounceSaveRef.current) {
            clearTimeout(debounceSaveRef.current);
            debounceSaveRef.current = null;
        }
        if (localSettingsRef.current) doSave(localSettingsRef.current);
    }, [doSave]);

    if (globalSettingsQuery.isLoading || !localSettings) {
        return <div>Loading...</div>;
    }

    const update = (partial: Partial<IGlobalStorefrontSettings>) => {
        const updated = { ...(localSettingsRef.current ?? localSettings), ...partial };
        setLocalSettings(updated);
        scheduleSave();
    };

    const updateNow = (partial: Partial<IGlobalStorefrontSettings>) => {
        const updated = { ...(localSettingsRef.current ?? localSettings), ...partial };
        setLocalSettings(updated);
        doSave(updated);
    };

    const isEnterprise = orgEntitlements.data?.storefrontTier === STOREFRONT_TIER_ENTERPRISE;

    return (
        <AssetSourceProvider value={{ storefrontId: STOREFRONT_ASSET_GLOBAL }}>
        <div>
            <h1>Appearance</h1>
            <Pivot selectedKey={tab} onLinkClick={(item) => setTab(item?.props.itemKey)}>
                <PivotItem itemKey='headerFooter' headerText='Header & Footer'>
                    <Stack tokens={{ childrenGap: '1.5em' }} style={{ maxWidth: 600, marginTop: '1em' }}>
                        <h2 style={{ margin: 0 }}>Header</h2>
                        <Setting id='storefront.appearance.headerLogo'>
                            <ImageEditor
                                label='Header Logo'
                                value={localSettings.headerLogo}
                                onChange={(v) => updateNow({ headerLogo: v })}
                                height={50}
                                imageId='global-header-logo'
                                onClear={() => updateNow({ headerLogo: '' })}
                                imageType='Storefront'
                            />
                        </Setting>
                        <Setting id='storefront.appearance.headerLink'>
                            <TextField
                                label='Header Link'
                                description='URL the header logo links to'
                                value={localSettings.headerLink ?? ''}
                                onChange={(_, v) => update({ headerLink: v ?? '' })}
                                onBlur={saveNow}
                            />
                        </Setting>
                        <Setting id='storefront.appearance.useHeaderSearch'>
                            <ToggleField
                                inline
                                label='Default Use Header Search'
                                checked={localSettings.useHeaderSearch ?? false}
                                onChange={(_, v) => updateNow({ useHeaderSearch: v ?? false })}
                                toolTip='When enabled, a search bar will appear in the storefront header instead of the main content area.'
                            />
                        </Setting>

                        <h2 style={{ margin: 0 }}>Footer</h2>
                        <Setting id='storefront.appearance.includeFooter'>
                            <ToggleField
                                inline
                                label='Default Include Footer'
                                checked={localSettings.includeFooter ?? false}
                                onChange={(_, v) => updateNow({ includeFooter: v ?? false })}
                            />
                        </Setting>
                        <ConditionalSetting when={!!localSettings.includeFooter} ids={GLOBAL_FOOTER_FIELD_IDS}>
                            <Stack tokens={{ childrenGap: '1em' }}>
                                <Setting id='storefront.appearance.footerLogo'>
                                    <ImageEditor
                                        label='Footer Logo'
                                        value={localSettings.footerLogo}
                                        onChange={(v) => updateNow({ footerLogo: v })}
                                        height={50}
                                        imageId='global-footer-logo'
                                        onClear={() => updateNow({ footerLogo: '' })}
                                        imageType='Storefront'
                                    />
                                </Setting>
                                <Setting id='storefront.appearance.footerContactDetails'>
                                    <RichTextField
                                        label='Footer Contact Details'
                                        value={localSettings.footerContactDetails ?? ''}
                                        onChange={(v) => update({ footerContactDetails: v ?? '' })}
                                    />
                                </Setting>
                                <Setting id='storefront.appearance.footerText'>
                                    <RichTextField
                                        label='Footer Text'
                                        value={localSettings.footerText ?? ''}
                                        onChange={(v) => update({ footerText: v ?? '' })}
                                    />
                                </Setting>
                                <Setting id='storefront.appearance.footerPhone'>
                                    <TextField
                                        label='Footer Phone'
                                        value={localSettings.footerPhone ?? ''}
                                        onChange={(_, v) => update({ footerPhone: v ?? '' })}
                                        onBlur={saveNow}
                                    />
                                </Setting>
                                <Setting id='storefront.appearance.footerEmail'>
                                    <TextField
                                        label='Footer Email'
                                        value={localSettings.footerEmail ?? ''}
                                        onChange={(_, v) => update({ footerEmail: v ?? '' })}
                                        onBlur={saveNow}
                                    />
                                </Setting>
                                <h3 style={{ margin: 0 }}>Social Links</h3>
                                <Setting id='storefront.appearance.footerFacebook'>
                                    <TextField
                                        label='Facebook'
                                        prefix='facebook.com/'
                                        value={localSettings.footerFacebook ?? ''}
                                        onChange={(_, v) => update({ footerFacebook: v ?? '' })}
                                        onBlur={saveNow}
                                    />
                                </Setting>
                                <Setting id='storefront.appearance.footerYouTube'>
                                    <TextField
                                        label='YouTube'
                                        prefix='youtube.com/'
                                        value={localSettings.footerYoutube ?? ''}
                                        onChange={(_, v) => update({ footerYoutube: v ?? '' })}
                                        onBlur={saveNow}
                                    />
                                </Setting>
                                <Setting id='storefront.appearance.footerLinkedIn'>
                                    <TextField
                                        label='LinkedIn'
                                        prefix='linkedin.com/in/'
                                        value={localSettings.footerLinkedIn ?? ''}
                                        onChange={(_, v) => update({ footerLinkedIn: v ?? '' })}
                                        onBlur={saveNow}
                                    />
                                </Setting>
                                <Setting id='storefront.appearance.footerTwitter'>
                                    <TextField
                                        label='Twitter'
                                        prefix='twitter.com/'
                                        value={localSettings.footerTwitter ?? ''}
                                        onChange={(_, v) => update({ footerTwitter: v ?? '' })}
                                        onBlur={saveNow}
                                    />
                                </Setting>
                                <Setting id='storefront.appearance.footerX'>
                                    <TextField
                                        label='X'
                                        prefix='x.com/'
                                        value={localSettings.footerX ?? ''}
                                        onChange={(_, v) => update({ footerX: v ?? '' })}
                                        onBlur={saveNow}
                                    />
                                </Setting>
                                <Setting id='storefront.appearance.footerBluesky'>
                                    <TextField
                                        label='Bluesky'
                                        prefix='bsky.app/profile/'
                                        value={localSettings.footerBluesky ?? ''}
                                        onChange={(_, v) => update({ footerBluesky: v ?? '' })}
                                        onBlur={saveNow}
                                    />
                                </Setting>
                            </Stack>
                        </ConditionalSetting>
                    </Stack>
                </PivotItem>

                <PivotItem itemKey='banners' headerText='Banners'>
                    <Stack tokens={{ childrenGap: '1em' }} style={{ marginTop: '1em' }}>
                        <div style={{ width: 200 }}>
                            <Setting id='storefront.appearance.bannerHeight'>
                                <NumberField
                                    label='Banner Height'
                                    toolTip='Default height for the banner container. Recommended when banners vary in dimensions to prevent screen jumping.'
                                    value={localSettings.bannerHeight}
                                    onChange={(v) => updateNow({ bannerHeight: v })}
                                    suffix='px'
                                />
                            </Setting>
                        </div>
                        <StorefrontBannerEditor isGlobal={true} />
                    </Stack>
                </PivotItem>

                <PivotItem itemKey='links' headerText='Links'>
                    <div style={{ marginTop: '1em' }}>
                        <StorefrontLinksEditor isGlobal={true} />
                    </div>
                </PivotItem>

                {isEnterprise && (
                    <PivotItem itemKey='themes' headerText='Themes'>
                        <div style={{ marginTop: '1em' }}>
                            <StorefrontThemeManagement />
                        </div>
                    </PivotItem>
                )}

                <PivotItem itemKey='legacy' headerText='Legacy Settings'>
                    <MessageBar>
                        These settings set global defaults. We recommend configuring styles at the individual storefront level instead — use the storefront's Pages editor or Look & Feel settings.
                    </MessageBar>
                    <Stack tokens={{ childrenGap: '1.5em' }} style={{ maxWidth: 600 }}>
                        <h2>Branding</h2>
                        <Setting id='storefront.appearance.favicon'>
                            <ImageEditor
                                label='Favicon'
                                value={localSettings.favicon}
                                onChange={(v) => updateNow({ favicon: v })}
                                height={96}
                                imageId='global-favicon'
                                onClear={() => updateNow({ favicon: '' })}
                                imageType='Storefront'
                            />
                        </Setting>
                        <Setting id='storefront.appearance.name'>
                            <TextField
                                label='Global Name'
                                value={localSettings.name ?? ''}
                                onChange={(_, v) => update({ name: v ?? '' })}
                                onBlur={saveNow}
                            />
                        </Setting>
                        <Setting id='storefront.appearance.title'>
                            <TextField
                                label='Default Title'
                                value={localSettings.title ?? ''}
                                onChange={(_, v) => update({ title: v ?? '' })}
                                onBlur={saveNow}
                            />
                        </Setting>
                        <Setting id='storefront.appearance.bannerText'>
                            <TextField
                                label='Default Banner Text'
                                value={localSettings.bannerText ?? ''}
                                onChange={(_, v) => update({ bannerText: v ?? '' })}
                                onBlur={saveNow}
                            />
                        </Setting>

                        <h2>Theme</h2>
                        <StorefrontThemeSelection
                            theme={localSettings.theme}
                            onChange={(v) => updateNow({ theme: v })}
                        />
                        <ConditionalSetting when={localSettings.theme === 'CUSTOM'} ids={GLOBAL_THEME_CUSTOM_FIELD_IDS}>
                            <Stack tokens={{ childrenGap: '0.75em' }}>
                                <Setting id='storefront.appearance.menuBarColor'>
                                    <CompactColorPicker
                                        label='Menu Bar Color'
                                        color={localSettings.menuBarColor ?? '#fff'}
                                        onChange={(v) => updateNow({ menuBarColor: v })}
                                    />
                                </Setting>
                                <Setting id='storefront.appearance.menuBarTextColor'>
                                    <CompactColorPicker
                                        label='Menu Bar Text Color'
                                        color={localSettings.menuBarTextColor ?? '#000'}
                                        onChange={(v) => updateNow({ menuBarTextColor: v })}
                                    />
                                </Setting>
                                <Setting id='storefront.appearance.backgroundColor'>
                                    <CompactColorPicker
                                        label='Background Color'
                                        color={localSettings.backgroundColor ?? '#fff'}
                                        onChange={(v) => updateNow({ backgroundColor: v })}
                                    />
                                </Setting>
                                <Setting id='storefront.appearance.textColor'>
                                    <CompactColorPicker
                                        label='Text Color'
                                        color={localSettings.textColor ?? '#000'}
                                        onChange={(v) => updateNow({ textColor: v })}
                                    />
                                </Setting>
                            </Stack>
                        </ConditionalSetting>

                        <h2>Typography & Layout</h2>
                        <Setting id='storefront.appearance.headerFont'>
                            <FontPicker
                                label='Header Font'
                                selectedFont={localSettings.headerFont}
                                onChange={(v) => updateNow({ headerFont: v })}
                            />
                        </Setting>
                        <Setting id='storefront.appearance.defaultFont'>
                            <FontPicker
                                label='Default Font'
                                selectedFont={localSettings.defaultFont}
                                onChange={(v) => updateNow({ defaultFont: v })}
                            />
                        </Setting>
                        <div style={{ width: 200 }}>
                            <Setting id='storefront.appearance.maxWidth'>
                                <NumberField
                                    label='Max Width'
                                    value={localSettings.maxWidth}
                                    placeholder='800'
                                    suffix='px'
                                    onChange={(v) => updateNow({ maxWidth: v })}
                                />
                            </Setting>
                        </div>

                        <h2>Navigation</h2>
                        <Setting id='storefront.appearance.showCategories'>
                            <Checkbox
                                label='Default Show Categories'
                                checked={localSettings.useCategories ?? false}
                                onChange={(_, v) => updateNow({ useCategories: v ?? false })}
                            />
                        </Setting>
                    </Stack>
                </PivotItem>
            </Pivot>
        </div>
        </AssetSourceProvider>
    );
};

export default StorefrontGlobalAppearancePage;
